
import Mail from '@/assets/svg/icons/mail.svg';
import Minus from '@/assets/svg/icons/minus.svg';
import Phone from '@/assets/svg/icons/phone.svg';
import Plus from '@/assets/svg/icons/plus.svg';
import ShapeEven from '@/assets/svg/icons/shape-even.svg';
import ShapeBottomFull from '@/assets/svg/overlays/shape-bottom-full.svg';
import ShapeBottomLeft from '@/assets/svg/overlays/shape-bottom-left.svg';
import ShapeTopRight from '@/assets/svg/overlays/shape-top-right.svg';
import gsap, { SplitText, ScrollTrigger } from '~/plugins/gsap';
import throttle from 'lodash/throttle';
import Vue from 'vue';
import { TransformedCmAbstract, TransformedCmStageService } from '~/transforms/transformModules';

export default Vue.extend({
  components: { ShapeEven, ShapeBottomFull, Plus, Minus, Mail, Phone, ShapeTopRight, ShapeBottomLeft },
  props: {
    data: {
      type: Object as () => TransformedCmStageService['data'],
      required: true,
    },
  },
  data() {
    return {
      contactsExpanded: false,
      windowWidth: null as any,
      throttledResize: null as any,
      saveHeadlineText: '' as string,
    };
  },
  async fetch() {
    // Prefetch segments and roles data since ContactItem components will need them
    if (this.data.contacts && this.data.contacts.length > 0) {
      await Promise.all([this.$loadDataSource('segment'), this.$loadDataSource('job-rollen')]);
    }
  },
  mounted() {
    this.throttledResize = throttle(this.resizeAnimationEvent, 10);
    this.initResizeEvent();
    setTimeout(() => {
      this.initAnimation();
    }, 500);
    const navInline = document.querySelector('header') as HTMLElement;
    if (navInline && navInline.querySelector('.c-nav-inline')) {
      (this.$refs.stage as HTMLElement).classList.add('cm-stage-service--nav');
    }
    this.adjustImageWrapperProportions();
  },
  computed: {
    alertTheme() {
      if (this.$marketSettings.alertThemeOverride) return 'light';
      // Use white theme for green stages and green theme for white stages
      return 'white';
    },
    cmAbstractData(): TransformedCmAbstract['data'] {
      return {
        theme: 'green',
        copy: this.data.copy,
        text: undefined,
        author: undefined,
        anchorId: this.data.anchorId,
      };
    },
  },
  methods: {
    adjustImageWrapperProportions() {
      const imageWrapper = this.$el.querySelector('.cm-stage-service__image-wrapper') as HTMLElement;
      const image = this.$el.querySelector('.cm-stage-service__image') as HTMLElement;

      if (imageWrapper && image) {
        if (this.data.imageFormat !== '3to2') {
          // adjust proportions of imageWrapper to clip-path proportions
          imageWrapper.style.height = image.offsetHeight * 0.925 + 'px';
        } else {
          if (window.innerWidth < 1024) {
            imageWrapper.style.height = image.offsetHeight + 'px';
          } else {
            imageWrapper.style.height = 'auto';
          }
        }
      }
    },

    initAnimation() {
      if (!process.client) return;

      gsap.config({ nullTargetWarn: false });

      const module = this.$el as HTMLElement;
      gsap.to(module, { opacity: 1, duration: 0.4, ease: 'curveAppear' });

      const clipPathList = this.$el.querySelectorAll<SVGPathElement>('#cm-stage-service__overlay-clipPath path');

      if (this.data.imageFormat === '3to2') {
        gsap.set(clipPathList[0], { rotation: 180 });
        gsap.set(clipPathList[1], { rotation: -180 });
      } else {
        gsap.set(clipPathList, { rotation: 180 });
        gsap.set(clipPathList[1], { autoAlpha: 0 });
        gsap.set(clipPathList[1], { autoAlpha: 1, delay: 0.6 });
      }

      gsap.to(clipPathList[0], { rotation: 0, duration: 0.6, ease: 'curveTech', force3D: true });
      gsap.to(clipPathList[1], { rotation: 0, duration: 0.6, ease: 'curveTech', force3D: true, delay: 0.2 });

      // ---> headline / copy / button

      let copyMoveX = 100;
      if (module.offsetWidth) {
        copyMoveX = module.offsetWidth / 3;
      }

      const headline = this.$el.querySelector<HTMLElement>('.cm-stage-service__headline');
      const headlineSpan = this.$el.querySelector<HTMLElement>('.cm-stage-service__headline span');

      if (headlineSpan) {
        if (this.saveHeadlineText === '') {
          this.saveHeadlineText = headlineSpan.innerHTML;
        }
        headlineSpan.innerHTML = this.saveHeadlineText;
      }

      if (window.innerWidth <= 560) {
        gsap.set(headline, { autoAlpha: 0, x: 20 });
        gsap.to(headline, { autoAlpha: 1, x: 0, duration: 0.7, ease: 'curveAppear', delay: 0.2 });
      } else {
        const headlineLines = new SplitText(headlineSpan, { type: 'lines' }).lines;
        gsap.set(headline, { autoAlpha: 1 });
        gsap.set(headlineLines, { autoAlpha: 0, x: '+=' + copyMoveX });
        gsap.to(headlineLines, { autoAlpha: 1, x: '-=' + copyMoveX, stagger: 0.1, duration: 0.7, ease: 'curveAppear' });
      }

      // ---> shape
      gsap.set(['.cm-stage-service__shape g path'], { strokeWidth: 0, x: -30, y: -30 });
      gsap.to(['.cm-stage-service__shape g path'], {
        strokeWidth: 30,
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'curveTech',
        delay: 1.2,
      });

      // -------------> shape-parallax-scrubbing

      const shape = this.$el.querySelector('.cm-stage-service__shape') as HTMLElement;

      ScrollTrigger.saveStyles(shape);

      gsap.set(shape, { y: 0 });
      gsap.fromTo(
        shape,
        { y: 0 },
        {
          y: () => {
            const h = shape.getBoundingClientRect().height;
            return h;
          },
          ease: 'none',
          scrollTrigger: {
            trigger: module,
            start: 'top top',
            endTrigger: shape,
            end: 'bottom top',
            scrub: 0.8,
            invalidateOnRefresh: true,
          },
        }
      );
    },

    resizeAnimationEvent() {
      // update animation if width has changed:
      if (
        (window.innerWidth > 560 && this.windowWidth <= 560) ||
        (window.innerWidth <= 560 && this.windowWidth > 560)
      ) {
        gsap.set('.cm-stage-service__headline', { autoAlpha: 0 });
        this.initAnimation();
      }
      this.windowWidth = window.innerWidth;
      //
      this.adjustImageWrapperProportions();
    },

    initResizeEvent() {
      window.addEventListener('resize', this.throttledResize);
    },
  },
});
